import { useState } from "react";
import {
  useOmniGeneratePostPhotoUploadUrlMutation,
  useOmniConfirmPostPhotoUploadMutation,
  useOmniPostPhotosQuery,
} from "../../../lib/apollo/graphql/omni/omni-generated";

export const usePostPhotoUpload = (postId: string) => {
  const [uploading, setUploading] = useState(false);

  // Query to fetch existing photos
  const {
    data: photosData,
    loading: photosLoading,
    refetch: refetchPhotos,
  } = useOmniPostPhotosQuery({
    variables: { postId },
    skip: !postId,
  });

  // Mutation to generate upload URL
  const [generateUploadUrl] = useOmniGeneratePostPhotoUploadUrlMutation();

  // Mutation to confirm upload
  const [confirmUpload] = useOmniConfirmPostPhotoUploadMutation();

  const uploadPhoto = async (file: File) => {
    try {
      setUploading(true);

      // Step 1: Generate upload URL
      const { data: uploadUrlData } = await generateUploadUrl({
        variables: {
          postId,
          fileName: file.name,
          mimeType: file.type,
        },
      });

      if (!uploadUrlData?.generatePostPhotoUploadUrl) {
        throw new Error("Failed to generate upload URL");
      }

      const { uploadUrl, uploadSessionId } =
        uploadUrlData.generatePostPhotoUploadUrl;

      // Step 2: Upload file to the signed URL
      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": file.type,
        },
        body: file,
      });

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        throw new Error(`Upload failed with status ${uploadResponse.status}: ${errorText}`);
      }

      // Step 3: Confirm upload
      const { data: confirmData } = await confirmUpload({
        variables: {
          postId,
          uploadSessionId,
        },
      });

      if (!confirmData?.confirmPostPhotoUpload) {
        throw new Error("Failed to confirm upload");
      }

      // Refetch photos to update the list
      await refetchPhotos();

      return confirmData.confirmPostPhotoUpload;
    } catch (error) {
      console.error("Error uploading photo:", error);
      throw error;
    } finally {
      setUploading(false);
    }
  };

  return {
    photos: photosData?.postPhotos || [],
    photosLoading,
    uploading,
    uploadPhoto,
    refetchPhotos,
  };
};
